import { ChangeEvent, FC, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ControllerRenderProps } from 'react-hook-form/dist/types/controller';
import { useTranslation } from 'react-i18next';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Stack,
  TextFieldProps,
  Tooltip,
  useTheme,
} from '@mui/material';

import { ReactComponent as FormInfo } from '@/assets/icons/form-info-icon.svg';
import { REGEX_NOT_DIGITS_AND_DOT } from '@/shared/constants';
import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';
import { camelToSnakeCase } from '@/shared/lib/strings';
import { FORM_LABEL_SX } from '@/shared/ui/form-fields/fieldStyles';
import { FormError, FormLabel } from '@/shared/ui/styled';

const countDecimals = (raw: string, decimalSeparator: '.' | ','): number => {
  const idx = raw.lastIndexOf(decimalSeparator);
  return idx === -1 ? 0 : raw.length - idx - 1;
};

const countDots = (raw: string): number => raw.split('.').length - 1;

const committedDecimal = (raw: string): string | number => {
  const parsed = parseFloat(raw.replace(/,$/, '').replaceAll('.', '').replaceAll(',', '.'));
  return isNaN(parsed) ? raw : parsed;
};

const withTypedDotAsDecimal = (raw: string, previous: string, cursor: number | null): string => {
  if (raw.includes(',') || countDots(raw) <= countDots(previous)) return raw;
  const dotIdx = cursor !== null && raw[cursor - 1] === '.' ? cursor - 1 : raw.lastIndexOf('.');
  return `${raw.slice(0, dotIdx)},${raw.slice(dotIdx + 1)}`;
};

type Props = {
  name: string;
  isRequired?: boolean;
  label?: string;
  extraLabel?: string;
  tooltip?: string;
  valueType?: 'string' | 'number' | 'float' | 'price';
  /** Cap on typed decimal digits for `valueType="float"` (e.g. a BNR exchange rate needs 4,
   *  not the 2 a price/dimension field wants). Ignored by `"price"`, which is always 2. */
  maxDecimals?: number;
  /** Pads the displayed value with trailing zeros up to this many decimals once committed
   *  (e.g. a manually typed exchange rate of "5,2" reads as "5,20"). Doesn't truncate — a value
   *  typed with more decimals than this still shows them all, up to `maxDecimals`. */
  minDecimals?: number;
  acceptDotAsDecimal?: boolean;
  validation?: string | RegExp;
  validateOnChange?: boolean;
  onChange?: (value: string | number | undefined) => void;
} & Omit<TextFieldProps, 'onChange'>;

const useDebouncedValidation = (trigger: (name: string) => void, delay = 300) => {
  const timeoutRef = useRef<number | null>(null);

  return useCallback(
    (fieldName: string) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        trigger(fieldName);
      }, delay);
    },
    [trigger, delay],
  );
};

const RhfTextField: FC<Props> = ({
  name,
  isRequired,
  label,
  extraLabel,
  tooltip,
  disabled,
  valueType,
  maxDecimals = 2,
  minDecimals = 0,
  acceptDotAsDecimal = false,
  validation,
  validateOnChange = false,
  ...muiProps
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { trigger } = useFormContext();

  const debouncedValidate = useDebouncedValidation(trigger, 300);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [inputDisplay, setInputDisplay] = useState<string | null>(null);

  const inputElRef = useRef<HTMLInputElement | null>(null);
  const pendingCursorRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (pendingCursorRef.current !== null && inputElRef.current) {
      inputElRef.current.setSelectionRange(pendingCursorRef.current, pendingCursorRef.current);
      pendingCursorRef.current = null;
    }
  });

  const isNumericType = valueType === 'number' || valueType === 'float' || valueType === 'price';

  const type = useMemo(() => {
    if (muiProps.type === 'password') {
      return showPassword ? 'text' : 'password';
    }

    return muiProps.type;
  }, [muiProps.type, showPassword]);

  const iconStyles = { fontSize: 17, color: theme.palette.text.primary, marginRight: 5 };

  const formatNumericValue = useCallback(
    (value: string | number | undefined): string => {
      if (typeof value === 'number') {
        const decimals = countDecimals(String(value), '.');
        return value.toLocaleString('ro-RO', {
          minimumFractionDigits: minDecimals,
          maximumFractionDigits: Math.max(minDecimals, 2, decimals),
        });
      }
      if (typeof value === 'string' && value !== '' && value !== '-') {
        const hasComma = value.includes(',');
        const parsed = hasComma
          ? parseFloat(value.replaceAll('.', '').replaceAll(',', '.'))
          : parseFloat(value);
        if (!isNaN(parsed)) {
          const decimals = countDecimals(value, hasComma ? ',' : '.');
          return parsed.toLocaleString('ro-RO', {
            minimumFractionDigits: minDecimals,
            maximumFractionDigits: Math.max(minDecimals, 2, decimals),
          });
        }
      }
      return '';
    },
    [minDecimals],
  );

  const getValue = useCallback(
    (value: string | number | undefined) => {
      if (isNumericType) {
        if (inputDisplay !== null) return inputDisplay;
        return formatNumericValue(value);
      }
      return value ?? '';
    },
    [isNumericType, inputDisplay, formatNumericValue],
  );

  const formatLiveNumeric = useCallback((raw: string): string => {
    if (raw === '' || raw === '-') return raw;
    const commaIdx = raw.lastIndexOf(',');
    const intPart = commaIdx >= 0 ? raw.slice(0, commaIdx) : raw;
    const decPart = commaIdx >= 0 ? raw.slice(commaIdx) : '';
    const sign = intPart.startsWith('-') ? '-' : '';
    const digits = intPart.replace(/[^0-9]/g, '');
    if (digits === '') return sign + decPart;
    const formatted = parseInt(digits, 10).toLocaleString('ro-RO');
    return sign + formatted + decPart;
  }, []);

  const handleNumericFocus = useCallback(
    (value: string | number | undefined) => {
      if (!isNumericType) return;
      if (typeof value === 'number') {
        setInputDisplay(value === 0 ? '' : formatNumericValue(value));
      } else if (value) {
        setInputDisplay(value.includes(',') ? formatLiveNumeric(value) : formatNumericValue(value));
      } else {
        setInputDisplay('');
      }
    },
    [isNumericType, formatNumericValue, formatLiveNumeric],
  );

  const handleNumericBlur = useCallback(() => {
    if (isNumericType) setInputDisplay(null);
  }, [isNumericType]);

  const onChange = useCallback(
    (field: ControllerRenderProps, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (validation && !RegExp(validation).test(e.target.value)) {
        return;
      }

      const rawValue =
        valueType === 'float' && acceptDotAsDecimal
          ? withTypedDotAsDecimal(e.target.value, inputDisplay ?? '', e.target.selectionStart)
          : e.target.value;

      let newValue;
      if (!valueType || valueType === 'string') {
        newValue = e.target.value ?? '';
      } else if (valueType === 'number') {
        if (!/^-?[\d.]*$/.test(e.target.value)) {
          return;
        }

        const rawDigits = e.target.value.replace(/[^0-9]/g, '');
        const prevDigits = (inputDisplay ?? '').replace(/[^0-9]/g, '');
        if (rawDigits.length > 15 && rawDigits.length > prevDigits.length) return;

        newValue =
          e.target.value === '' || e.target.value === '-'
            ? e.target.value
            : parseInt(e.target.value.replaceAll(',', '').replaceAll('.', ''), 10);

        if (typeof newValue === 'number' && !isNaN(newValue)) {
          if (newValue < (muiProps?.inputProps?.min ?? 0)) {
            enqueueSnackbar(
              `${t('Number must be equal or greater than')} ${muiProps.inputProps?.min ?? 0}`,
            );
            return;
          }
          if (muiProps.inputProps?.max !== undefined && newValue > muiProps.inputProps.max) {
            enqueueSnackbar(`${t('Number must be equal or less than')} ${muiProps.inputProps.max}`);
            return;
          }
        }
      } else if (valueType === 'float') {
        if (!/^-?[\d,.]*$/.test(rawValue)) {
          return;
        }

        const rawFloatDigits = rawValue.split(',')[0].replace(/[^0-9]/g, '');
        const prevFloatDigits = (inputDisplay ?? '').split(',')[0].replace(/[^0-9]/g, '');
        if (rawFloatDigits.length > 13 && rawFloatDigits.length > prevFloatDigits.length) return;

        const floatDecimal = rawValue.split(',')[1] ?? '';
        const prevFloatDecimal = (inputDisplay ?? '').split(',')[1] ?? '';
        if (floatDecimal.length > maxDecimals && floatDecimal.length > prevFloatDecimal.length) {
          return;
        }

        newValue =
          rawValue === '' ||
          rawValue === '-' ||
          rawValue[rawValue.length - 1] === ',' ||
          (rawValue.includes(',') && rawValue[rawValue.length - 1] === '0')
            ? rawValue
            : parseFloat(rawValue.replaceAll('.', '').replaceAll(',', '.'));

        const parsedForValidation = parseFloat(
          rawValue.replace(/,$/, '').replaceAll('.', '').replaceAll(',', '.'),
        );
        if (!isNaN(parsedForValidation)) {
          if (
            muiProps?.inputProps?.min !== undefined &&
            parsedForValidation < muiProps.inputProps.min
          ) {
            enqueueSnackbar(
              `${t('Number must be equal or greater than')} ${muiProps.inputProps.min}`,
            );
            return;
          }
          if (
            muiProps.inputProps?.max !== undefined &&
            parsedForValidation > muiProps.inputProps.max
          ) {
            enqueueSnackbar(`${t('Number must be equal or less than')} ${muiProps.inputProps.max}`);
            return;
          }
        }
      } else if (valueType === 'price') {
        if (!/^-?[\d,.]*$/.test(e.target.value)) {
          return;
        }

        const rawPriceDigits = e.target.value.split(',')[0].replace(/[^0-9]/g, '');
        const prevPriceDigits = (inputDisplay ?? '').split(',')[0].replace(/[^0-9]/g, '');
        if (rawPriceDigits.length > 13 && rawPriceDigits.length > prevPriceDigits.length) return;

        const priceDecimal = e.target.value.split(',')[1] ?? '';
        const prevPriceDecimal = (inputDisplay ?? '').split(',')[1] ?? '';
        if (priceDecimal.length > 2 && priceDecimal.length > prevPriceDecimal.length) return;

        newValue =
          e.target.value === '' ||
          e.target.value === '-' ||
          e.target.value[e.target.value.length - 1] === ',' ||
          (e.target.value.includes(',') && e.target.value[e.target.value.length - 1] === '0')
            ? e.target.value
            : parseFloat(e.target.value.replaceAll('.', '').replaceAll(',', '.'));
      } else {
        newValue = e.target.value.replaceAll(REGEX_NOT_DIGITS_AND_DOT, '');
      }

      if (isNumericType) {
        const oldCursor = e.target.selectionStart ?? 0;
        const nonDotsBefore = rawValue.slice(0, oldCursor).replace(/\./g, '').length;
        const newDisplay = formatLiveNumeric(rawValue);
        let cursor = newDisplay.length;
        let count = 0;
        for (let i = 0; i < newDisplay.length; i++) {
          if (count === nonDotsBefore) {
            cursor = i;
            break;
          }
          if (newDisplay[i] !== '.') count++;
        }
        pendingCursorRef.current = cursor;
        setInputDisplay(newDisplay);
      }
      field.onChange(newValue);
      muiProps.onChange?.(newValue);

      if (validateOnChange) {
        debouncedValidate(name);
      }
    },
    [
      validation,
      valueType,
      isNumericType,
      formatLiveNumeric,
      muiProps,
      enqueueSnackbar,
      t,
      validateOnChange,
      debouncedValidate,
      name,
      inputDisplay,
      maxDecimals,
      acceptDotAsDecimal,
    ],
  );

  return (
    <Controller
      name={name}
      render={({ field, fieldState: { error }, formState: { isSubmitting } }) => (
        <FormControl fullWidth error={!!error}>
          {!!label && (
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <FormLabel sx={FORM_LABEL_SX}>
                {label}{' '}
                {!!extraLabel && <span style={{ textTransform: 'none' }}>{extraLabel}</span>}
              </FormLabel>

              {!!tooltip && (
                <Tooltip title={tooltip}>
                  <span>
                    <Button type="button" sx={{ minWidth: 0, padding: '0.25rem' }} disableRipple>
                      <FormInfo />
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
          )}

          <MuiTextField
            {...field}
            {...muiProps}
            inputRef={inputElRef}
            placeholder={
              muiProps.placeholder
                ? muiProps.placeholder
                : isRequired
                  ? t('required')
                  : t('optional')
            }
            label={undefined}
            type={type}
            value={getValue(field.value)}
            onFocus={() => handleNumericFocus(field.value)}
            onBlur={() => {
              handleNumericBlur();
              if (acceptDotAsDecimal && typeof field.value === 'string') {
                field.onChange(committedDecimal(field.value));
              }
              field.onBlur();
            }}
            onChange={(e) => onChange(field, e)}
            disabled={isSubmitting || disabled}
            error={Boolean(error)}
            sx={{
              paddingBottom: error ? 0 : '1rem',
              ...muiProps.sx,
              '& .MuiOutlinedInput-root': {
                height: muiProps.multiline ? 'auto' : '41px',
                paddingLeft: '16px !important',
                borderRadius: '0.75rem !important',
                '& fieldset': {
                  borderRadius: '0.75rem !important',
                  transition: 'all 0.3s ease-in-out',
                },
                '&:not(.Mui-disabled):hover': {
                  borderColor: 'black !important',
                },
                '&.Mui-focused': {
                  borderColor: '#4592fa !important',
                  boxShadow: '0 0 0 1px #4592fa !important',
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#A4A4B6',
                opacity: 1,
              },
              '& .MuiInputAdornment-positionEnd': {
                marginRight: '0.5rem !important',
              },
              '& .MuiInputBase-input': {
                padding: '0 !important',
              },
              '& textarea': {
                fontWeight: 500,
                fontSize: '0.75rem',
                lineHeight: 1.6,
                color: theme.palette.grey[800],
              },
            }}
            autoComplete="off"
            inputProps={{
              value: getValue(field.value),
              id: `${camelToSnakeCase(name)}-input`,
              ...muiProps.inputProps,
              type: type,
            }}
            InputProps={{
              ...muiProps.InputProps,
              endAdornment:
                muiProps.type === 'password' ? (
                  <InputAdornment position="end">
                    <IconButton
                      style={{ backgroundColor: 'transparent' }}
                      onClick={() => setShowPassword((prevState) => !prevState)}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOff style={iconStyles} />
                      ) : (
                        <Visibility style={iconStyles} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ) : (
                  muiProps.InputProps?.endAdornment || null
                ),
            }}
          />

          {!!error && (
            <FormError>
              {error?.type === 'invalid_type' ? t('errors.required_field') : error.message}
            </FormError>
          )}
        </FormControl>
      )}
    />
  );
};

export default RhfTextField;
